import { Link } from '@tanstack/react-router';
import { config } from '@shared/config';
import { useFooterConfig } from '@entities/footer';
import { DEFAULT_FOOTER_CONFIG } from '@fanini/shared-types';
import type { FooterSection } from '@fanini/shared-types';

/**
 * Rendert eine einzelne Footer-Sektion mit Titel und Links.
 *
 * @param section - Die Footer-Sektion mit Titel und Links
 */
function FooterSectionNav({ section }: { section: FooterSection }) {
  return (
    <div className="flex flex-col">
      <div className="footer-section-title">{section.title}</div>
      <nav aria-label={`${section.title}-Navigation`}>
        <ul className="flex flex-col gap-2.5 list-none">
          {section.links.map((link) => (
            <li key={`${link.label}-${link.url}`}>
              <a href={link.url} className="footer-link">
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/**
 * Footer der Anwendung.
 * Ultra-Stil mit Gradient-Hintergrund, prominentem Fan-Logo, Brush-Stroke-Trenner
 * und dynamischen Spalten-Navigationen aus der API.
 *
 * @example
 * ```tsx
 * <Footer />
 * ```
 */
export function Footer() {
  const currentYear = new Date().getFullYear();
  const { data: footerConfig } = useFooterConfig();
  const sections = footerConfig?.sections ?? DEFAULT_FOOTER_CONFIG.sections;

  return (
    <>
      <style>{`
        .footer-root {
          background: linear-gradient(180deg, #1a1a18 0%, #111110 100%);
          color: rgba(255,255,255,0.5);
          position: relative;
          overflow: hidden;
        }

        .footer-brush-divider {
          width: 100%;
          height: 60px;
          background-image: url('/assets/brush-stroke/brush-stroke-iron-v1.png');
          background-size: cover;
          background-position: center top;
          background-repeat: no-repeat;
          opacity: 0.15;
          pointer-events: none;
          position: absolute;
          top: 0;
          left: 0;
        }

        .footer-bg-text {
          position: absolute;
          bottom: 0;
          right: 0;
          font-size: clamp(80px, 15vw, 160px);
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgba(255,255,255,0.015);
          line-height: 1;
          pointer-events: none;
          user-select: none;
          white-space: nowrap;
        }

        .footer-logo-mark {
          height: 3rem;
          width: auto;
          filter: drop-shadow(0 0 12px rgba(184,78,72,0.3));
          transition: filter 0.3s ease;
        }

        .footer-logo-link:hover .footer-logo-mark {
          filter: drop-shadow(0 0 20px rgba(184,78,72,0.55));
        }

        .footer-section-title {
          font-size: 0.65rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.25em;
          color: rgba(255,255,255,0.25);
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .footer-section-title::before {
          content: '';
          display: block;
          width: 1rem;
          height: 2px;
          background-color: #b84e48;
          flex-shrink: 0;
        }

        .footer-link {
          font-size: 0.875rem;
          color: rgba(255,255,255,0.45);
          text-decoration: none;
          transition: color 0.15s ease, padding-left 0.2s ease;
          display: inline-block;
        }

        .footer-link:hover {
          color: #ffffff;
          padding-left: 0.25rem;
        }

        .footer-bottom {
          border-top: 1px solid rgba(255,255,255,0.06);
          padding: 1.5rem 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
        }

        @media (min-width: 640px) {
          .footer-bottom {
            flex-direction: row;
          }
        }
      `}</style>

      <footer className="footer-root">
        {/* Brush-Stroke oben als Trenner */}
        <div className="footer-brush-divider" aria-hidden="true" />

        {/* Dekorativer Hintergrundtext */}
        <div className="footer-bg-text" aria-hidden="true">
          ULTRAS
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Main grid */}
          <div className="grid gap-10 pt-16 pb-12 sm:grid-cols-2 lg:grid-cols-4">
            {/* Brand */}
            <div className="flex flex-col gap-5 lg:col-span-1">
              <Link
                to="/"
                className="footer-logo-link inline-flex items-end gap-3 no-underline"
                aria-label="Faninitiative Spandau — Startseite"
              >
                <img
                  src="/logos/fan-logo-white.svg"
                  alt="Faninitiative Spandau Logo"
                  className="footer-logo-mark"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Link>
              <p className="text-sm leading-relaxed text-white/40 max-w-xs">
                Das offizielle Fan-Portal der Faninitiative Spandau e.V. — Dein Treffpunkt für
                Events, Gemeinschaft und Leidenschaft.
              </p>
              {/* Discord-Link */}
              <a
                href={config.discordInviteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 no-underline group"
                aria-label="Faninitiative Discord-Server beitreten"
              >
                <svg
                  viewBox="0 0 71 55"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  className="h-4 w-4 flex-shrink-0 text-[#5865F2] transition-colors duration-150 group-hover:text-[#7289da]"
                >
                  <path
                    d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.44077 45.4204 0.52529C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.52529C25.5141 0.44329 25.4218 0.40104 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"
                    fill="currentColor"
                  />
                </svg>
                <span className="text-sm text-white/40 transition-colors duration-150 group-hover:text-white/70">
                  Discord beitreten
                </span>
              </a>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 bg-accent" aria-hidden="true" />
                <span className="text-xs font-black uppercase tracking-widest text-white/30">
                  Seit 2024
                </span>
              </div>
            </div>

            {/* Dynamische Sektionen */}
            {sections.map((section) => (
              <FooterSectionNav key={section.title} section={section} />
            ))}
          </div>

          {/* Bottom bar */}
          <div className="footer-bottom">
            <p className="text-xs text-white/25">
              &copy; {currentYear} Faninitiative Spandau e.V. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 bg-accent" aria-hidden="true" />
              <span className="text-xs text-white/25">Mit Leidenschaft für den Sport</span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
